import DashboardDesktopFilters from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/DashboardDesktopFilters';
import DashboardMobileFilters from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/DashboardMobileFilters';
import useDashboardFilters from '@/hooks/admin/AdminDashboardTab/useDashboardFilters';
import useDashboardMetrics from '@/hooks/admin/AdminDashboardTab/useDashboardMetrics';
import usePropertyYearlyMetrics from '@/hooks/admin/AdminDashboardTab/usePropertyYearlyMetrics';
import { useIsMobile } from '@/hooks/use-mobile';
import { useProperties } from '@/hooks/useProperties';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useContracts } from '@/hooks/useContracts';

import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import MetricCard from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/MetricCard';
import MetricCardSummary from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/MetricCardSummary';
import IncomeByOriginChart from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/IncomeByOriginChart';
import PropertyIncomeExpensesChart from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertyIncomeExpensesChart';
import PropertyAccumulatedChart from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertyAccumulatedChart';
import { formatCurrency, formatCurrencyNoDecimals } from '@/utils/shared/currencyUtils';
import { formatDateRange } from '@/utils/shared/dateUtils';
import { Calendar, CreditCard, DollarSign, TrendingUp, Home, Moon } from 'lucide-react';
import { Skeleton } from "@/components/ui/skeleton";
import AdminPageHeader from '@/components/v2/atoms/admin/layout/AdminPageHeader';

const AdminDashboardTab = () => {
  const isMobile = useIsMobile();

  const {
    data: properties = [],
    isLoading: isLoadingProperties,
    error: errorProperties,
    refetch: refetchProperties,
  } = useProperties();

  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  const {
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    selectedProperties,
    setSelectedProperties,
    selectedOrganizations,
    setSelectedOrganizations,
    yearOptions,
    monthOptions,
    handlePropertyChange,
    goToPreviousMonth,
    goToNextMonth,
    canGoToPreviousMonth,
    canGoToNextMonth,
  } = useDashboardFilters();

  const { metrics: filteredData, isLoading: isLoadingMetrics } = useDashboardMetrics({
    selectedYear,
    selectedMonth,
    selectedProperties,
    selectedOrganizations,
    properties,
  });

  const { data: yearlyMetrics, isLoading: isLoadingYearly } = usePropertyYearlyMetrics({
    year: parseInt(selectedYear),
    propertyIds: selectedProperties.length > 0 ? selectedProperties : undefined,
    organizationIds: selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
  });

  const getMonthDescription = () => {
    const monthLabel = monthOptions.find((m) => m.value === selectedMonth)?.label ?? '';
    return `${monthLabel} ${selectedYear}`;
  };

  const metrics = {
    receivedIncome: filteredData?.receivedIncome ?? 0,
    futureIncome: filteredData?.futureIncome ?? 0,
    expenses: filteredData?.totalExpenses ?? 0,
    occupancyRate: filteredData?.occupancyRate ?? 0,
    averageCostPerNight: filteredData?.averageCostPerNight ?? 0,
    nightsToRent: filteredData?.nightsToRent ?? 0,
    occupancyRate7Days: filteredData?.occupancyRate7Days ?? 0,
    occupancyRate7DaysDateStart: filteredData?.occupancyRate7DaysDateStart ?? '',
    occupancyRate7DaysDateEnd: filteredData?.occupancyRate7DaysDateEnd ?? '',
    occupancyRate15Days: filteredData?.occupancyRate15Days ?? 0,
    occupancyRate15DaysDateStart: filteredData?.occupancyRate15DaysDateStart ?? '',
    occupancyRate15DaysDateEnd: filteredData?.occupancyRate15DaysDateEnd ?? '',
  };

  const error = errorProperties;

  return (
    <div className="space-y-6">
      {error && <TabErrorBanner />}

      {/* Header: Title + Filters */}
      <div className="space-y-3">
        <AdminPageHeader title="Dashboard" />

        {/* Filters */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          {isMobile ? (
            <DashboardMobileFilters
              handlePropertyChange={handlePropertyChange}
              monthOptions={monthOptions}
              properties={properties}
              organizations={organizations}
              contracts={contracts}
              selectedMonth={selectedMonth}
              selectedProperties={selectedProperties}
              selectedOrganizations={selectedOrganizations}
              selectedYear={selectedYear}
              setSelectedMonth={setSelectedMonth}
              setSelectedYear={setSelectedYear}
              setSelectedOrganizations={setSelectedOrganizations}
              yearOptions={yearOptions}
              goToPreviousMonth={goToPreviousMonth}
              goToNextMonth={goToNextMonth}
              canGoToPreviousMonth={canGoToPreviousMonth()}
              canGoToNextMonth={canGoToNextMonth()}
            />
          ) : (
            <DashboardDesktopFilters
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              yearOptions={yearOptions}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              monthOptions={monthOptions}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
              selectedOrganizations={selectedOrganizations}
              setSelectedOrganizations={setSelectedOrganizations}
              properties={properties}
              organizations={organizations}
              contracts={contracts}
              isLoading={isLoadingProperties}
              goToPreviousMonth={goToPreviousMonth}
              goToNextMonth={goToNextMonth}
              canGoToPreviousMonth={canGoToPreviousMonth()}
              canGoToNextMonth={canGoToNextMonth()}
            />
          )}
        </div>
      </div>

      {/* Metrics Grid - 4x2 Layout */}
      
      {/* Fila 1: Financieros */}
      <div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Costo promedio x Noche"
            icon={<Calendar className="h-5 w-5" />}
            value={formatCurrencyNoDecimals(metrics.averageCostPerNight)}
            description={getMonthDescription()}
            isLoading={isLoadingMetrics}
            hint="Ingreso promedio por noche ocupada en el mes (incluye todos los ingresos recibidos divididos entre las noches reservadas pagadas)"
            variant="neutral"
          />

          <MetricCard
            title="Ingresos por recibir"
            icon={<TrendingUp className="h-5 w-5" />}
            value={formatCurrencyNoDecimals(metrics.futureIncome)}
            description={getMonthDescription()}
            isLoading={isLoadingMetrics}
            hint="Ingresos pendientes de recibir de reservaciones en el mes seleccionado"
            variant="info"
          />

          <MetricCard
            title="Ingresos recibidos"
            icon={<DollarSign className="h-5 w-5" />}
            value={formatCurrencyNoDecimals(metrics.receivedIncome)}
            description={getMonthDescription()}
            isLoading={isLoadingMetrics}
            hint="Ingresos ya recibidos en el mes seleccionado"
            variant="success"
          />

          <MetricCard
            title="Gastos realizados"
            icon={<CreditCard className="h-5 w-5" />}
            value={formatCurrencyNoDecimals(metrics.expenses)}
            description={getMonthDescription()}
            isLoading={isLoadingMetrics}
            hint="Total de gastos registrados en el mes seleccionado"
            variant="destructive"
          />
        </div>
      </div>

      {/* Fila 2: Operaciones */}
      <div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Noches por rentar"
            icon={<Moon className="h-5 w-5" />}
            value={metrics.nightsToRent}
            description={getMonthDescription()}
            isLoading={isLoadingMetrics}
            hint="Número de noches disponibles para renta en el mes seleccionado (excluye días bloqueados y reservados)"
            variant="neutral"
          />

          <MetricCard
            title="Tasa de ocupación"
            icon={<Home className="h-5 w-5" />}
            value={`${Math.round(metrics.occupancyRate)}%`}
            description={getMonthDescription()}
            isLoading={isLoadingMetrics}
            hint="Porcentaje de noches ocupadas del total de noches disponibles en el mes"
            variant="neutral"
          />

          <MetricCard
            title="Tasa ocupación 7 días"
            icon={<Home className="h-5 w-5" />}
            value={`${metrics.occupancyRate7Days}%`}
            description={
              metrics.occupancyRate7DaysDateStart && metrics.occupancyRate7DaysDateEnd
                ? formatDateRange(metrics.occupancyRate7DaysDateStart, metrics.occupancyRate7DaysDateEnd)
                : "Calculando..."
            }
            isLoading={isLoadingMetrics}
            hint="Porcentaje de noches ocupadas en los próximos 7 días a partir de hoy"
            variant="neutral"
          />

          <MetricCard
            title="Tasa ocupación 15 días"
            icon={<Home className="h-5 w-5" />}
            value={`${metrics.occupancyRate15Days}%`}
            description={
              metrics.occupancyRate15DaysDateStart && metrics.occupancyRate15DaysDateEnd
                ? formatDateRange(metrics.occupancyRate15DaysDateStart, metrics.occupancyRate15DaysDateEnd)
                : "Calculando..."
            }
            isLoading={isLoadingMetrics}
            hint="Porcentaje de noches ocupadas en los próximos 15 días a partir de hoy"
            variant="neutral"
          />
        </div>
      </div>

      {/* Detalle Section - Summary + Chart */}
      {!isMobile && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <MetricCardSummary
                receivedIncome={metrics.receivedIncome}
                futureIncome={metrics.futureIncome}
                totalExpenses={metrics.expenses}
                formatCurrency={formatCurrency}
                isLoading={isLoadingMetrics}
                year={parseInt(selectedYear)}
                month={parseInt(selectedMonth)}
                propertyIds={selectedProperties.length > 0 ? selectedProperties : undefined}
                organizationIds={selectedOrganizations.length > 0 ? selectedOrganizations : undefined}
              />
            </div>

            <div className="lg:col-span-1">
              <IncomeByOriginChart
                year={parseInt(selectedYear)}
                month={parseInt(selectedMonth)}
                propertyIds={selectedProperties.length > 0 ? selectedProperties : undefined}
                organizationIds={selectedOrganizations.length > 0 ? selectedOrganizations : undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tendencias Anuales - Gráficas de Ingresos/Gastos */}
      {!isMobile && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Tendencias del Año {selectedYear}</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PropertyIncomeExpensesChart
              incomeData={yearlyMetrics?.monthly_income}
              expensesData={yearlyMetrics?.monthly_expenses}
              isLoading={isLoadingYearly}
              year={parseInt(selectedYear)}
            />
            <PropertyAccumulatedChart
              data={yearlyMetrics?.accumulated_profit}
              isLoading={isLoadingYearly}
              year={parseInt(selectedYear)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardTab;
